import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetCollections } from "../../hooks/collections/useGetCollections";
import { useDeleteCollection } from "../../hooks/collections/useDeleteCollection";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import CollectionsTableRow from "./Table/CollectionsTableRow";
import { CollectionsTableColumns } from "./Table/CollectionsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const CollectionsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("cms.manage"), // Usando cms.manage como permiso base para Collections
            create: has("cms.manage"),
            edit: has("cms.manage"),
            delete: has("cms.manage"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [statusFilter, setStatusFilter] = useState("");

    // CRUD Hooks
    const { deleteColl, isDelete } = useDeleteCollection();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/collections",
        deleteSingle: deleteColl,
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            is_active: statusFilter !== "" ? statusFilter : undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, statusFilter, order]
    );

    const { isLoading, error, collections, meta } = useGetCollections(params);

    // تجهيز الفلاتر لـ TableLayout
    const tableFilters = [
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: (val) => setStatusFilter(val),
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ]
        }
    ];

    const renderRow = (rowParams) => (
        <CollectionsTableRow
            key={rowParams.item.id}
            collection={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    const title = isArabic ? "المجموعات" : "Collections";
    const subtitle = isArabic ? "إدارة مجموعات المنتجات" : "Manage product collections";

    return (
        <TableLayout
            title={title}
            subtitle={subtitle}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? "إضافة مجموعة" : "Add Collection"}
            onAddClick={() => navigate("/collections/create")}
            addButtonVisible={perms.create}
            isLoading={isLoading}
            error={error}
            items={collections}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={CollectionsTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            menuDisableEdit={!perms.edit}
            menuDisableDelete={!perms.delete}
            isDeletingSingle={isDelete}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, collections)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onBulkDelete={handleBulkDelete}
            bulkDeleteDisabled={!perms.delete}
            filters={tableFilters}
            onClearAllFilters={() => {
                setStatusFilter("");
            }}
            emptyLabelAr={isArabic ? "لا توجد مجموعات" : "No collections"}
            emptyLabelEn={isArabic ? "لا توجد مجموعات" : "No collections"}
        />
    );
};

export default CollectionsTable;
